import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import api from '../utils/axiosConfig';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * MS_PER_DAY;
const ONE_DAY_MS = MS_PER_DAY;

function getDaysUntilExpiry(periodEnd) {
  if (!periodEnd) return null;
  const end = new Date(periodEnd);
  const now = new Date();
  return Math.ceil((end - now) / MS_PER_DAY);
}

function ExpirationWarningBanner({ userPlans: userPlansProp, onPlanUpdated }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(null);
  const [userPlans, setUserPlans] = useState(userPlansProp || {});

  useEffect(() => {
    if (userPlansProp) {
      setUserPlans(userPlansProp);
      return;
    }
    api.get('/api/plans/me')
      .then((res) => setUserPlans(res.data || {}))
      .catch(() => setUserPlans({}));
  }, [userPlansProp]);

  useEffect(() => {
    const handler = () => {
      api.get('/api/plans/me').then((res) => setUserPlans(res.data || {})).catch(() => {});
    };
    window.addEventListener('planSubscriptionUpdated', handler);
    return () => window.removeEventListener('planSubscriptionUpdated', handler);
  }, []);

  const warnings = [];
  for (const [roleType, plan] of Object.entries(userPlans || {})) {
    if (!plan?.currentPeriodEnd || plan.status !== 'active') continue;
    const daysLeft = getDaysUntilExpiry(plan.currentPeriodEnd);
    if (daysLeft == null || daysLeft > 7) continue;
    const isCanceled = plan.cancelAtPeriodEnd;
    if (!isCanceled) continue;
    const severity = daysLeft <= 1 ? 'critical' : daysLeft <= 7 ? 'warning' : null;
    if (severity) {
      warnings.push({
        roleType,
        planName: plan.name,
        daysLeft,
        severity,
        canReactivate: !!plan.stripeSubscriptionId,
      });
    }
  }

  const handleReactivate = async (roleType) => {
    try {
      setLoading(roleType);
      await api.post('/api/stripe/subscription/reactivate', { roleType });
      onPlanUpdated?.();
      window.dispatchEvent(new Event('planSubscriptionUpdated'));
    } catch (err) {
      console.error('Reactivate failed:', err);
    } finally {
      setLoading(null);
    }
  };

  if (warnings.length === 0) return null;

  return (
    <div className='space-y-2 py-2'>
      {warnings.map((w) => (
        <Alert
          key={w.roleType}
          variant={w.severity === 'critical' ? 'destructive' : 'default'}
          className='border-amber-500/50 bg-amber-500/10 dark:bg-amber-500/5'
        >
          <AlertTriangle className='h-4 w-4' />
          <AlertTitle>
            {w.severity === 'critical'
              ? t('plans.expiryWarningDay', { plan: w.planName })
              : t('plans.expiryWarningWeek', { plan: w.planName, days: String(w.daysLeft) })}
          </AlertTitle>
          <AlertDescription className='flex flex-wrap items-center gap-2'>
            <span>
              {w.severity === 'critical'
                ? t('plans.expiryWarningDayDesc')
                : t('plans.expiryWarningWeekDesc')}
            </span>
            {w.canReactivate && (
              <Button
                size='sm'
                variant='outline'
                onClick={() => handleReactivate(w.roleType)}
                disabled={!!loading}
              >
                {loading === w.roleType ? (
                  <Loader2 className='h-4 w-4 animate-spin' />
                ) : (
                  t('plans.continueSubscription')
                )}
              </Button>
            )}
          </AlertDescription>
        </Alert>
      ))}
    </div>
  );
}

export default ExpirationWarningBanner;
